import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { subscribeToAllOrders, type Order } from '../../../utils/firebase';
import { Search, ArrowUpDown } from 'lucide-react';
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
export function AdminOrders() {
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
      setLoadFailed(true);
    }, 10000);
    const unsub = subscribeToAllOrders(data => {
      clearTimeout(timer);
      setOrders(data);
      setLoading(false);
      setLoadFailed(false);
    });
    return () => {
      clearTimeout(timer);
      unsub();
    };
  }, []);
  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter(order => {
      const matchesSearch = !term || order.id.toLowerCase().includes(term) || order.email.toLowerCase().includes(term);
      const matchesStatus = statusFilter === 'all' || order.fulfillmentStatus === statusFilter;
      const hasDigital = order.lineItems.some(i => i.type === 'book');
      const hasPhysical = order.lineItems.some(i => i.type === 'supplement');
      const matchesType = typeFilter === 'all' || typeFilter === 'digital' && hasDigital || typeFilter === 'physical' && hasPhysical;
      return matchesSearch && matchesStatus && matchesType;
    }).sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [orders, search, statusFilter, typeFilter, sortDirection]);
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-364" data-mp="2sdfv4rsa3km2">
      <h1 className="text-2xl font-bold text-[#111111] mb-2" data-id="element-365" data-mp="2xbzeqljegkb9">Orders</h1>
      <p className="text-sm text-[#5A5A5A] mb-8" data-id="element-366" data-mp="2xe3j14l7nlc2">
        Every order placed across the RD Ready store.
      </p>

      {loadFailed && <div role="status" className="mb-8 bg-white border border-[#E5E5E5] border-l-4 border-l-[#D97706] rounded-lg p-4" data-id="element-367" data-mp="2nlo0o6604uqn">
          <p className="text-sm font-bold text-[#111111] mb-1" data-id="element-368" data-mp="3bah22i5e7usn">
            Live order data unavailable
          </p>
          <p className="text-sm text-[#5A5A5A]" data-id="element-369" data-mp="3g4bjtg1ftzp3">
            {isPreview ? 'You are in preview mode and not signed in, so Firestore is not returning orders.' : 'Orders could not be read from Firestore. Security rules may be blocking access to the orders collection.'}
          </p>
        </div>}

      <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden" data-id="element-370" data-mp="19doowxc9jh9u">
        <div className="p-4 border-b border-[#E5E5E5] flex flex-col sm:flex-row gap-4 justify-between items-center bg-[#FAFAFA]" data-id="element-371" data-mp="lo41o292txur">
          <div className="relative w-full sm:w-96" data-id="element-372" data-mp="gqufcvgzi2b5">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-373" data-mp="vmtj5tx29gah" />
            <label htmlFor="order-search" className="sr-only" data-id="element-374" data-mp="qnqguh8gto67">
              Search orders
            </label>
            <input id="order-search" type="text" placeholder="Search orders or emails..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate" data-id="element-375" data-mp="271qgdr3efbsx" />
          </div>
          <div className="flex gap-3 w-full sm:w-auto" data-id="element-376" data-mp="klf0sc8qrg3t">
            <label htmlFor="status-filter" className="sr-only" data-id="element-377" data-mp="pljj87t7mkwo">
              Filter by status
            </label>
            <select id="status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate" data-id="element-378" data-mp="ukw8t0bgu62y">
              <option value="all" data-id="element-379" data-mp="18caooya2tr5s">All Statuses</option>
              <option value="pending" data-id="element-380" data-mp="18e5f2s44abh9">Pending</option>
              <option value="shipped" data-id="element-381" data-mp="1dc6vf5a5rwp1">Shipped</option>
              <option value="delivered" data-id="element-382" data-mp="1d9az1ldylg2p">Delivered</option>
              <option value="digital_delivered" data-id="element-383" data-mp="1ib0ah253fvrb">Digital Delivered</option>
            </select>
            <label htmlFor="type-filter" className="sr-only" data-id="element-384" data-mp="1seyjk4yabg8y">
              Filter by type
            </label>
            <select id="type-filter" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="px-3 py-2 bg-white border border-[#E5E5E5] rounded-md text-sm text-[#111111] focus:outline-none focus:border-tealslate" data-id="element-385" data-mp="1ndhkmezd23v0">
              <option value="all" data-id="element-386" data-mp="2b3ve89xlgarp">All Types</option>
              <option value="physical" data-id="element-387" data-mp="26520cgeeahr0">Physical</option>
              <option value="digital" data-id="element-388" data-mp="267n4jc2619rx">Digital</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-389" data-mp="21cqxb4ocyfu8">
          <table className="w-full text-left border-collapse" data-id="element-390" data-mp="1xdrv3ru0gx6h">
            <thead data-id="element-391" data-mp="1xbfh1prdj6yp">
              <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]" data-id="element-392" data-mp="2tuwcswusy4aq">
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-393" data-mp="2tx8z1m7lf0tl">
                  Order
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-394" data-mp="n6m81eljh2jm">
                  <button type="button" onClick={() => setSortDirection(d => d === 'desc' ? 'asc' : 'desc')} className="flex items-center gap-1 uppercase hover:text-[#111111]" data-id="element-395" data-mp="s68n7tkxgsv2">
                    Date <ArrowUpDown className="w-3 h-3" data-id="element-396" data-mp="4fczp8ni8jtm" />
                  </button>
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-397" data-mp="9g8zba0tpjt8">
                  Customer
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-398" data-mp="3vmpfbj8qop7f">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-399" data-mp="1oxaqk71iudyu">
                  Total
                </th>
                <th className="px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wider" data-id="element-400" data-mp="1tsyhorzkdh6z">
                  Payment
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E5E5]" data-id="element-401" data-mp="39c78wvu042v7">
              {loading ? <tr data-id="element-402" data-mp="3o4hhdvl5vf0m">
                  <td colSpan={6} className="px-6 py-12 text-center" data-id="element-403" data-mp="3o4znrl1jf9m9">
                    <span className="inline-block w-6 h-6 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" role="status" aria-label="Loading orders" data-id="element-404" data-mp="3javgf0od0zkn" />
                  </td>
                </tr> : filteredOrders.length === 0 ? <tr data-id="element-405" data-mp="5x1tjvhm4om3">
                  <td colSpan={6} className="px-6 py-12 text-center text-[#5A5A5A]" data-id="element-406" data-mp="5ylbrrmtx9qi">
                    No orders to show.
                  </td>
                </tr> : filteredOrders.map(order => <tr key={order.id} className="hover:bg-[#FAFAFA] transition-colors" data-id="element-407" data-mp="1lupqacop9lwr">
                    <td className="px-6 py-4" data-id="element-408" data-mp="1qsrax21rxtac">
                      <Link to={`/admin/orders/${order.id}${isPreview ? '?preview=true' : ''}`} className="text-tealslate font-bold hover:underline" data-id="element-409" data-mp="1vlaohz0goof5">
                        #{order.id.slice(0, 8).toUpperCase()}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#5A5A5A]" data-id="element-410" data-mp="17w85amq6inij">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-411" data-mp="1hup0jvw5hoae">
                      <p className="text-sm font-medium text-[#111111]" data-id="element-412" data-mp="1hvgw56pqzm0o">
                        {order.shippingAddress?.line1 ? 'Physical order' : 'Digital customer'}
                      </p>
                      <p className="text-xs text-[#5A5A5A]" data-id="element-413" data-mp="hlpjocgiply7">{order.email}</p>
                    </td>
                    <td className="px-6 py-4" data-id="element-414" data-mp="cmcon4fht8kj">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${order.fulfillmentStatus === 'pending' ? 'bg-amber-100 text-amber-800' : order.fulfillmentStatus === 'shipped' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-415" data-mp="cno441iaw4rh">
                        {order.fulfillmentStatus.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-[#111111]" data-id="element-416" data-mp="3l3r03a01w4zz">
                      {formatPrice(order.total)}
                    </td>
                    <td className="px-6 py-4" data-id="element-417" data-mp="3g5xvumib5l2c">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${order.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-418" data-mp="1ja9vygb4pm9z">
                        {order.status}
                      </span>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}